<template lang="pug">
.rg-img(
  :class="[ratioClass, { 'no-bg': transparent, contain: autoHeight || $attrs.contain !== undefined || contain }]"
  :style="{ 'background-color': customBackground }"
  v-element-visibility="setVisible"
)
  div(v-if="ratioClass === 'r-custom'" :style="{ 'padding-top': `${(1 / ratio) * 100}%` }")
  video(
    v-if="renderedSrc && isVideo"
    ref="videoRef"
    :class="{'enable-video-controls': enableVideoControls}"
    :src="cloudinarySrc"
    v-bind="videoAttrs"
  )
  //- alt falls back to "" for alt-less data passed via v-bind (dev still warns about the missing prop):
  //- an omitted alt attribute makes screen readers read out the image URL
  img(
    v-else-if="renderedSrc"
    ref="imgRef"
    @load="imgLoad()"
    :src="cloudinarySrc"
    :srcset="srcSet"
    :class="{loaded: loaded || $attrs.ssr !== undefined, eager: eager}"
    :alt="alt ?? ''"
    :aria-label="ariaLabel"
    :loading="loadingAttr"
    :fetchpriority="fetchPriority"
  )
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useAttrs, watch } from 'vue'
import { vElementVisibility } from '@vueuse/components'
import { getCloudinaryUrl } from '../../js/helpers'

const props = withDefaults(defineProps<{
  src?: string,
  width?: number,
  height?: number,
  background?: string,
  /**
   * Required: describe the image, or pass "" when it's decorative or repeats nearby text.
   * There's deliberately no default, since any default would be wrong for most images.
   */
  alt: string,
  autoHeight?: boolean,
  ariaLabel?: string,
  enableVideoControls?: boolean,
  contain?: boolean,
  fetchPriority?: string,
  additionalTransforms?: string[],
  // Native pixel width of the source image, used to cap high-density srcset candidates
  imgWidth?: number,
}>(), {
  width                : 308,
  background           : '#f8f8f8',
  autoHeight           : false,
  enableVideoControls  : false,
  contain              : false,
  fetchPriority        : 'auto',
  additionalTransforms : () => [],
})

// Small images (logos, badges) get 2x/3x srcset candidates so they stay sharp on high-DPI screens.
// Larger images are excluded since the extra bytes outweigh the benefit.
const HIDPI_MAX_WIDTH = 400
const HIDPI_DENSITIES = [2, 3]

const attrs = useAttrs()

const videoRef = ref<HTMLVideoElement | null>(null)
const imgRef = ref<HTMLImageElement | null>(null)

// Whether the image is visible on the screen or not
const visible = ref(false)
// Whether the image has been loaded from the network
const loaded = ref(false)
const cached = ref(false)
const fast = ref(false)
let videoPlayInt: NodeJS.Timeout | undefined = undefined

const eager = computed<boolean>(() => attrs.eager !== undefined || (attrs.lazy === undefined && (attrs.ssr !== undefined || props.width < 150)) || cached.value || fast.value)
const loadingAttr = computed<'eager' | 'lazy'>(() => eager.value ? 'eager' : 'lazy')
const isVideo = computed<boolean>(() => typeof props.src === 'string' && (props.src.indexOf('.mp4') !== -1 || props.src.indexOf('.mov') !== -1))
const ratio = computed<number>(() => props.width / (props.height || props.width))
// By default, the image will have a gray background.
const transparent = computed<boolean>(() => props.background === 'transparent' || !!(props.src && props.src.indexOf && props.src.indexOf('.svg') !== -1))
const renderedSrc = computed<string | undefined>(() => attrs.ssr !== undefined || eager.value || visible.value ? props.src : undefined)

// Ratios are set by the padding of the element. This allows us to use position:cover to fill the container
const ratioClass = computed<'r-43' | 'r-169' | 'r-custom'>(() => {
  if (Math.abs(1.333 - ratio.value) < 0.01) {
    return 'r-43'
  } else if (Math.abs(1.777 - ratio.value) < 0.01) {
    return 'r-169'
  } else {
    return 'r-custom'
  }
})

const customBackground = computed<string | undefined>(() => {
  // Background can be set to a hex value
  if (!transparent.value && props.background && props.background !== '#f8f8f8') {
    return props.background
  }

  return undefined
})

const loadSize = computed<{width: number, height: number}>(() => {
  const width = props.width

  // If a height is defined, use that ( mult. by scale), otherwise compute a height based on the loadWidth and ratio
  const height = props.height
    ? Math.round(props.height)
    : Math.round((width * 100) / ratio.value) / 100

  return { width, height }
})

const cloudinarySrc = computed<string>(() => {
  return getCloudinaryUrl(
    props.src,
    loadSize.value.width,
    (!isVideo.value && !props.autoHeight) ? loadSize.value.height : undefined,
    !isVideo.value ? props.background : 'transparent',
    props.additionalTransforms
  ) || ''
})

const srcSet = computed<string | undefined>(() => {
  const { width, height } = loadSize.value
  if (isVideo.value || !width || width > HIDPI_MAX_WIDTH) {
    return undefined
  }

  const maxWidth = props.imgWidth || Infinity
  const candidates = [`${cloudinarySrc.value} 1x`]
  let prevWidth = width
  for (const density of HIDPI_DENSITIES) {
    // Don't request more pixels than the source has
    const candidateWidth = Math.min(Math.round(width * density), maxWidth)
    if (candidateWidth <= prevWidth) {
      break
    }

    const scale = candidateWidth / width
    const url = getCloudinaryUrl(
      props.src,
      candidateWidth,
      !props.autoHeight ? Math.round(height * scale) : undefined,
      props.background,
      props.additionalTransforms
    )
    // SVGs and non-Cloudinary sources come back unchanged, so there's nothing sharper to offer
    if (!url || url === cloudinarySrc.value) {
      return undefined
    }

    candidates.push(`${url} ${Math.round(scale * 100) / 100}x`)
    prevWidth = candidateWidth
  }

  return candidates.length > 1 ? candidates.join(', ') : undefined
})

const videoAttrs = computed(() => {
  const attrs: {[key: string]: string} = {}

  const poster = getCloudinaryUrl(props.src?.replace('.mp4', '.jpg').replace('.mov', '.jpg'), loadSize.value.width)
    ?.replace('assets.roguefitness.com', 'assets.roguefitness.com/video/upload/so_0')
  Object.assign(attrs, { loop: '', 'webkit-playsinline': 'true', playsinline: '', muted: 'muted', poster })

  attrs.preload = eager.value ? 'auto' : 'metadata'

  if (props.enableVideoControls) {
    attrs.controls = ''
  }

  return attrs
})

function checkVideoPlay() {
  const video = videoRef.value
  if (visible.value && video && video.currentTime === 0) {
    video.muted = true
    if (video.readyState < 2) {
      video.load()
    } else {
      video.play()
        .then(() => clearInterval(videoPlayInt))
        .catch(() => console.debug('Video autoplay error'))
    }
  }
}

function imgLoad() {
  loaded.value = true
}

function setVisible(isVisible: boolean) {
  if (isVisible) {
    visible.value = true
  }
}

watch(cloudinarySrc, () => {
  // Img src change is likely from dynamic media width change - change image immediately
  if (visible.value || loaded.value) {
    fast.value = true
  }

  if (imgRef.value?.complete) {
    cached.value = true
    loaded.value = true
  }
})

watch(isVideo, (val) => {
  // If this component is a video, we want to check on an interval whether it can be played or not.
  // This seems more reliable than listening to media events like oncanplay, especially for safari...
  clearInterval(videoPlayInt)
  if (val && typeof window !== 'undefined') {
    videoPlayInt = setInterval(() => checkVideoPlay(), 100)
  }
}, {
  immediate: true,
})

onMounted(() => {
  if (imgRef.value?.complete) {
    cached.value = true
    imgLoad()
  }

  if (isVideo.value && videoRef.value) {
    videoRef.value.muted = true
  }
})

onBeforeUnmount(() => {
  clearInterval(videoPlayInt)
})
</script>
<style lang="scss">
img,
video {
  backface-visibility: hidden;
}

.rg-img {
  background-color: #f8f8f8;
  position: relative;
  overflow: hidden;

  &.no-bg {
    background-color: transparent;
  }

  >img,
  >video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    min-height: 100%;
    object-fit: cover;
  }

  >img {
    opacity: 0;
    transition: opacity 150ms linear;

    &.eager {
      transition-duration: 0ms;

      &[src] {
        opacity: 1;
      }
    }

    &.loaded {
      opacity: 1;
    }
  }

  >video {
    pointer-events: none;
    border-bottom: 1px solid transparent;
    background: transparent;

    &.enable-video-controls {
      pointer-events: auto;
    }
  }

  &.contain > img, &.contain > video {
    position: absolute;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &:not(.r-custom)::after {
    content: '';
    display: block;
    padding-top: 100%;
    width: 0;
  }

  &.r-43::after {
    padding-top: 75%;
  }

  &.r-169::after {
    padding-top: 56.25%;
  }
}
</style>
